import { DismissRegular, TextBold24Regular, TextItalic24Regular, TextUnderline24Regular, DeleteRegular, PlayCircleRegular, AttachRegular, DocumentRegular, EditRegular, AddRegular } from "@fluentui/react-icons";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Card,
  CardHeader,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Title3,
  Button,
  Text,
  Field,
  Input,
  Textarea,
  Toolbar,
  ToolbarToggleButton,
  ToolbarDivider,
  CardPreview,
  Title1,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Spinner,
  MessageBar,
  MessageBarBody
} from "@fluentui/react-components";
import { useEffect, useState, useRef } from "react";
import supportQuestionService from "../../services/supportQuestionService";
import fileService from "../../services/fileService";
import { postExternal } from "../../services/externalService";
import { getSupportPageOptions, getSystemOptionsByID, updateSystemOption } from "../../services/systemOptionService";
import { SupportQuestion } from "../../models/supportQuestion";
import { SupportPageSystemOptions } from "../../models/systemOptions";
import { useAppContext } from "../../AppContext";
import theme from "quill/core/theme";
import ReactQuill from "react-quill";
import { deepCloneObject } from "../../utils/clone";
import { environment } from "../../environments/environment.prod";
import { getEnvironment } from "../../services/api";
import { RoleDTO } from "../../models/role";

type Props = {
  onClose: () => void;
  open: boolean;
};

const SupportDrawer = (props: Props) => {
  const { user } = useAppContext();
  const [supportQuestions, setSupportQuestions] = useState<SupportQuestion[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [media, setMedia] = useState<any[]>([]);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [mediaToUpload, setMediaToUpload] = useState<File | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<string>("");
  const [videoDialogOpen, setVideoDialogOpen] = useState<boolean>(false);
  const [mailConfirmationOpen, setMailConfirmationOpen] = useState<boolean>(false);
  const [supportMailLoading, setSupportMailLoading] = useState<boolean>(false);
  const [supportMail, setSupportMail] = useState({ header: "", body: "" });
  const [formError, setFormError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [systemOptions, setSystemOptions] = useState<SupportPageSystemOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);
  const [fileUploadDialogOpen, setFileUploadDialogOpen] = useState<boolean>(false);
  const [mediaUploadDialogOpen, setMediaUploadDialogOpen] = useState<boolean>(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [editSupportQuestionsDialogOpen, setEditSupportQuestionsDialogOpen] = useState<boolean>(false);
  const [editContactHtmlDialogOpen, setEditContactHtmlDialogOpen] = useState<boolean>(false);
  const [editedContactHtml, setEditedContactHtml] = useState<string>("");
  const [editingSupportQuestion, setEditingSupportQuestion] = useState<SupportQuestion | null>(null);
  const [supportQuestionFormData, setSupportQuestionFormData] = useState({ header: "", answer: "" });
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>("");
  const [successDialogOpen, setSuccessDialogOpen] = useState<boolean>(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState<string>("");
  const [deleteQuestionConfirmOpen, setDeleteQuestionConfirmOpen] = useState<boolean>(false);
  const [questionToDelete, setQuestionToDelete] = useState<number | null>(null);
  const apiurl = getEnvironment().apiUrl;
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if(props.open){
      const fetchData = async () => {
        setOptionsLoading(true);
        try{
          const [supportQuestionRes, sysOptionsRes] = await Promise.all([
            supportQuestionService.getSupportQuestions(),
            (user && user.roles.some((role: any) => role.roleType === 'General')) ? getSystemOptionsByID(1) : getSupportPageOptions()
          ]);
          if(user){
            const fileRes = await fileService.getSupportFiles();
            setFiles(fileRes.data.filter((f: any) => f.fileTypeID === 1));
            setMedia(fileRes.data.filter((f: any) => f.fileTypeID === 2));
          }
          setSupportQuestions(supportQuestionRes.data);
          setSystemOptions(sysOptionsRes);
          setLoaded(true);
        } catch(error) {
          console.error(error);
        } finally {
          setOptionsLoading(false);
        }
      }

      fetchData();
    }
  }, [props.open]);

  const sanitizeFileName = (name: string): string => {
    return name.replace(/[^\x00-\x7F]/g, "_");
  };

  const uploadFile = async (): Promise<void> => {
    if (!fileToUpload) return;
    
    const renamedFile = new File([fileToUpload], sanitizeFileName(fileToUpload.name), { type: fileToUpload.type });
    const formData: FormData = new FormData();
    formData.append('file', renamedFile);
    formData.append('displayFileName', fileToUpload.name);
    setLoaded(false);
    
    try {
      await fileService.createAttachmentFile(formData);
      await getFiles();
      setFileToUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setFileUploadDialogOpen(false);
    } catch (err) {
      console.error(err);
      setErrorDialogMessage('Ett fel uppstod vid uppladdning av filen');
      setErrorDialogOpen(true);
    } finally {
      setLoaded(true);
    }
  };

  const uploadMedia = async (): Promise<void> => {
    if (!mediaToUpload) return;

    const renamedFile = new File([mediaToUpload], sanitizeFileName(mediaToUpload.name), { type: mediaToUpload.type });
    const formData: FormData = new FormData();
    formData.append('file', renamedFile);
    formData.append('displayFileName', mediaToUpload.name);
    setLoaded(false);

    try {
      await fileService.createMediaFile(formData);
      await getFiles();
      setMediaToUpload(null);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
      setMediaUploadDialogOpen(false);
    } catch (err) {
      console.error(err);
      setErrorDialogMessage('Ett fel uppstod vid uppladdning av videon');
      setErrorDialogOpen(true);
    } finally {
      setLoaded(true);
    }
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>, type: string): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (type === 'media') {
      setMediaToUpload(file);
    } else {
      setFileToUpload(file);
    }
  };

  const deleteFile = (fileId: string): void => {
    setFileToDelete(fileId);
    setDeleteConfirmationOpen(true);
  };

  const confirmDeleteFile = async (): Promise<void> => {
    if (!fileToDelete) return;

    setLoaded(false);
    setDeleteConfirmationOpen(false);
    try {
      await fileService.deleteFile(fileToDelete);
      await getFiles();
    } catch (error) {
      console.error(error);
      setErrorDialogMessage('Ett fel uppstod vid borttagning av filen');
      setErrorDialogOpen(true);
    } finally {
      setLoaded(true);
      setFileToDelete(null);
    }
  };

  const getFiles = async (): Promise<void> => {
    try {
      const res = await fileService.getSupportFiles();
      setFiles(res.data.filter((f: any) => f.fileTypeID === 1));
      setMedia(res.data.filter((f: any) => f.fileTypeID === 2));
      setLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const selectVideo = (url: string): void => {
    setSelectedVideo(url);
    setVideoDialogOpen(true);
  };

  const sendSupportMail = async (): Promise<void> => {
    setFormError(false);
    setErrorMsg("");

    if (supportMail.header.length === 0 || supportMail.body.length === 0) {
      setFormError(true);
      setErrorMsg('Fyll i alla fält!');
      return;
    }

    if (!systemOptions?.supportQuestionLink) {
      setFormError(true);
      setErrorMsg('System options ej laddade');
      return;
    }

    if (!user?.user?.email) {
      setFormError(true);
      setErrorMsg('Användarinformation saknas');
      return;
    }

    setSupportMailLoading(true);

    const lime = {
      event: 'new_ticket',
      title: supportMail.header,
      user: user.user.email,
      content: supportMail.body
    };

    try {
      await postExternal(systemOptions.supportQuestionLink, lime);
      setSupportMail({ body: '', header: '' });
      setMailConfirmationOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogMessage('Ett fel uppstod vid skickande av meddelandet');
      setErrorDialogOpen(true);
    } finally {
      setSupportMailLoading(false);
    }
  };

  const saveContactHtml = async (): Promise<void> => {
    if (!systemOptions?.supportContactHtmlTemplate) {
      return;
    }
    setLoaded(false);
    const updatedOptions = deepCloneObject(systemOptions);
    updatedOptions.supportContactHtmlTemplate = editedContactHtml;
    try {
      await updateSystemOption(1, updatedOptions);
      setSystemOptions(updatedOptions);
      setEditContactHtmlDialogOpen(false);
    } catch (error) {
      console.error(error);
      setErrorDialogMessage('Ett fel uppstod vid uppdatering av kontaktinformation');
      setErrorDialogOpen(true);
    } finally {
      setLoaded(true);
    }
  };

  return (
    <>
    <Drawer
      size="large"
      position="end"
      open={props.open}
      modalType="non-modal"
      onOpenChange={(_event, data) => !data.open && props.onClose()}
    >
    <DrawerHeader>
      <DrawerHeaderTitle>
        <div className="flex items-start justify-between w-full">
          <Title1>
            Hjälp
          </Title1>
          <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose}>
          </Button>
        </div>
      </DrawerHeaderTitle>
    </DrawerHeader>
    <DrawerBody>
      <Card className="mt-5">
        <CardPreview>
          <div style={{display: "flex"}} className="flex items-start justify-between w-full">
            <Title3>Vanligt förekommande frågor</Title3>
            { user && user.roles?.some((r: RoleDTO) => r.roleType == 'General') && (
              <Button
              appearance="subtle"
              shape="circular"
              icon={<EditRegular />}
              onClick={() => setEditSupportQuestionsDialogOpen(true)}
              >
              </Button>
            )}
          </div>
        </CardPreview>
          <Accordion collapsible>
            {supportQuestions.map(sq => (
              <AccordionItem key={sq.supportQuestionID} value={sq.supportQuestionID}>
                <AccordionHeader>{sq.header}</AccordionHeader>
                <AccordionPanel>
                  <div className="rich-text-content" dangerouslySetInnerHTML={{__html: sq.answer}}></div>
                </AccordionPanel>
              </AccordionItem>
              )
            )}
          </Accordion>
      </Card>
      
      {user && <Card className="mt-5">
        <CardPreview>
          <Title3>Kontakta support</Title3>
        </CardPreview>
        
        {formError && (
          <MessageBar intent="error" className="mb-4">
            <MessageBarBody>{errorMsg}</MessageBarBody>
          </MessageBar>
        )}

        <Field
          label="Ämne"
          required
        >
          <Input 
            value={supportMail.header}
            onChange={(e) => setSupportMail({ ...supportMail, header: e.target.value })}
          />
        </Field>
        
        <Field
          label="Meddelande"
          required
        >
          <Textarea 
            resize="vertical"
            value={supportMail.body}
            onChange={(e) => setSupportMail({ ...supportMail, body: e.target.value })}
            rows={6}
          />
        </Field>
        
        <Button 
          appearance="primary" 
          onClick={sendSupportMail}
          disabled={supportMailLoading || optionsLoading}
        >
          {supportMailLoading ? <Spinner size="tiny" /> : 'Skicka meddelande'}
        </Button>
      </Card>}

      {user && (
        <>
          <Card className="mt-5">
            <CardPreview>
              <div style={{display: "flex"}} className="flex items-start justify-between w-full">
                <Title3>Bibliotek</Title3>
                { user && user.roles?.some((r: RoleDTO) => r.roleType == 'General') && (
                  <Button
                  appearance="subtle"
                  shape="circular"
                  icon={<EditRegular />}
                  onClick={() => setFileUploadDialogOpen(true)}
                  >
                  </Button>
                )}
              </div>
            </CardPreview>
            
            {files.length > 0 && (
              <div className="mt-4">
                {files.map(f => (
                  <div key={f.fileID} className="flex justify-between items-center py-2">
                    <a href={apiurl + '/' + f.url} target="_blank" rel="noopener noreferrer">{f.displayFileName || f.fileName}</a>
                    {user && user.roles.some((r: RoleDTO) => r.roleType == 'General') && <Button 
                      icon={<DeleteRegular />} 
                      appearance="subtle" 
                      size="small"
                      onClick={() => deleteFile(f.fileID)}
                    />}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
      
      {user && <Card className="mt-5">
        <CardPreview>
          <div style={{display: "flex"}} className="flex items-start justify-between w-full">
            <Title3>Utbildningsmaterial</Title3>
            { user && user.roles?.some((r: RoleDTO) => r.roleType == 'General') && (
              <Button
              appearance="subtle"
              shape="circular"
              icon={<EditRegular />}
              onClick={() => setMediaUploadDialogOpen(true)}
              >
              </Button>
            )}
          </div>
        </CardPreview>
        {media.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            {media.map(f => (
              <div 
                key={f.fileID} 
                className="border border-gray-300 rounded-lg overflow-hidden bg-white transition-all duration-200 cursor-pointer relative hover:-translate-y-1 hover:shadow-lg"
              >
                <div 
                  onClick={() => selectVideo(apiurl +"/"+ f.url)}
                  className="relative bg-black aspect-video flex items-center justify-center overflow-hidden"
                >
                  <video 
                    src={apiurl + "/" + f.url} 
                    className="w-full h-full object-cover"
                    muted
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                    <PlayCircleRegular className="text-white text-5xl opacity-90" />
                  </div>
                </div>
                <div className="p-3 flex justify-between items-center gap-2">
                  <Text 
                    weight="semibold" 
                    className="overflow-hidden truncate flex-1"
                    title={f.displayFileName || f.fileName}
                  >
                    {(f.displayFileName.length > 30 ? f.displayFileName.slice(0, 30) + "..." : f.displayFileName) || (f.fileName.length > 30 ? f.fileName.slice(0, 30) + "..." : f.fileName)}
                  </Text>
                  {user && user.roles.some((r: RoleDTO) => r.roleType == 'General') && (
                    <Button 
                      icon={<DeleteRegular />} 
                      appearance="subtle"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFile(f.fileID);
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Text>Inga videor tillgängliga</Text>
        )}
      </Card>}
      <Card className="my-5">
        <CardPreview>
          <div style={{display: "flex"}} className="flex items-start justify-between w-full">
            <Title3>Övriga kontaktuppgifter</Title3>
            { user && user.roles?.some((r: RoleDTO) => r.roleType == 'General') && (
              <Button
              appearance="subtle"
              shape="circular"
              icon={<EditRegular />}
              onClick={() => {
                setEditedContactHtml(systemOptions?.supportContactHtmlTemplate || '');
                setEditContactHtmlDialogOpen(true);
              }}
              >
              </Button>
            )}
          </div>
        </CardPreview>
        <div className="rich-text-content" dangerouslySetInnerHTML={{__html: systemOptions?.supportContactHtmlTemplate || ''}}></div>
      </Card>
    </DrawerBody>
    </Drawer>

    <Dialog open={videoDialogOpen} onOpenChange={(_, data) => setVideoDialogOpen(data.open)}>
      <DialogSurface className="min-w-[70vw]">
        <DialogBody>
          <DialogTitle>Video</DialogTitle>
          <DialogContent>
            <video
              controls
              controlsList="nodownload"
            >
              <source src={selectedVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setVideoDialogOpen(false)}>Stäng</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={mailConfirmationOpen} onOpenChange={(_, data) => setMailConfirmationOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Meddelande skickat</DialogTitle>
          <DialogContent>
            <Text>Ditt meddelande har skickats till supporten. Vi återkommer så snart vi kan.</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setMailConfirmationOpen(false)}>OK</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={fileUploadDialogOpen} onOpenChange={(_, data) => setFileUploadDialogOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Ladda upp fil</DialogTitle>
          <DialogContent>
            <Field label="Välj fil">
              <input 
                ref={fileInputRef}
                type="file" 
                onChange={(e) => onFileSelected(e, 'attachment')}
                className="hidden"
              />
              
              {!fileToUpload ? (
                <Button
                  appearance="outline"
                  icon={<AttachRegular />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!loaded}
                >
                  Välj fil
                </Button>
              ) : (
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded bg-gray-50">
                  <div className="flex items-center gap-2">
                    <DocumentRegular className="text-blue-600" />
                    <div>
                      <Text weight="semibold">{fileToUpload.name}</Text>
                      <br />
                      <Text size={200} className="text-gray-600">
                        {(fileToUpload.size / 1024).toFixed(2)} KB
                      </Text>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      appearance="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Byt fil
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => { setFileToUpload(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    >
                      Ta bort
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setFileUploadDialogOpen(false)}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={uploadFile}
              disabled={!fileToUpload || !loaded}
            >
              {!loaded ? <Spinner size="tiny" /> : 'Ladda upp'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={mediaUploadDialogOpen} onOpenChange={(_, data) => setMediaUploadDialogOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Ladda upp video</DialogTitle>
          <DialogContent>
            <Field label="Välj video">
              <input 
                ref={mediaInputRef}
                type="file" 
                accept="video/*"
                onChange={(e) => onFileSelected(e, 'media')}
                className="hidden"
              />
              
              {!mediaToUpload ? (
                <Button
                  appearance="outline"
                  icon={<AttachRegular />}
                  onClick={() => mediaInputRef.current?.click()}
                  disabled={!loaded}
                >
                  Välj video
                </Button>
              ) : (
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded bg-gray-50">
                  <div className="flex items-center gap-2">
                    <DocumentRegular className="text-blue-600" />
                    <div>
                      <Text weight="semibold">{mediaToUpload.name}</Text>
                      <br />
                      <Text size={200} className="text-gray-600">
                        {(mediaToUpload.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      appearance="outline"
                      onClick={() => mediaInputRef.current?.click()}
                    >
                      Byt fil
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => { setMediaToUpload(null); if (mediaInputRef.current) mediaInputRef.current.value = ''; }}
                    >
                      Ta bort
                    </Button>
                  </div>
                </div>
              )}
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setMediaUploadDialogOpen(false)}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={uploadMedia}
              disabled={!mediaToUpload || !loaded}
            >
              {!loaded ? <Spinner size="tiny" /> : 'Ladda upp'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={deleteConfirmationOpen} onOpenChange={(_, data) => setDeleteConfirmationOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Bekräfta borttagning</DialogTitle>
          <DialogContent>
            <Text>Är du säker på att du vill ta bort filen?</Text>
          </DialogContent>
          <DialogActions>
            <Button 
              appearance="secondary" 
              onClick={() => {
                setDeleteConfirmationOpen(false);
                setFileToDelete(null);
              }}
            >
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={confirmDeleteFile}
            >
              Ta bort
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={editSupportQuestionsDialogOpen} onOpenChange={(_, data) => setEditSupportQuestionsDialogOpen(data.open)}>
      <DialogSurface className="min-w-[70vw] max-h-[60vh] overflow-y-auto">
        <DialogBody>
          <DialogTitle>Redigera supportfrågor</DialogTitle>
          <DialogContent>
            <div className="mb-4">
              <Button 
                appearance="primary" 
                icon={<AddRegular />}
                onClick={() => {
                  setEditingSupportQuestion(null);
                  setSupportQuestionFormData({ header: "", answer: "" });
                  setIsFormOpen(true);
                }}
              >
                Lägg till ny fråga
              </Button>
            </div>
            
            {isFormOpen ? (
              <div className="mb-6 p-4 border-2 border-blue-600 rounded bg-blue-50">
                <Text weight="semibold" size={400} className="block mb-4">
                  {editingSupportQuestion ? 'Redigera fråga' : 'Ny fråga'}
                </Text>
                
                <Field label="Rubrik" required className="mb-4">
                  <Input
                    value={supportQuestionFormData.header}
                    onChange={(e) => setSupportQuestionFormData({ ...supportQuestionFormData, header: e.target.value })}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Ange rubrik..."
                  />
                </Field>
                
                <Field label="Svar" required className="mb-4">
                  <div onKeyDown={(e) => e.stopPropagation()}>
                    <ReactQuill
                      theme="snow"
                      className='rounded-quill-editor min-h-[200px] mb-10'
                      value={supportQuestionFormData.answer}
                      onChange={(value) => setSupportQuestionFormData({ ...supportQuestionFormData, answer: value })}
                      placeholder="Skriv svar här..."
                    />
                  </div>
                </Field>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    appearance="primary"
                    onClick={async () => {
                      if (!supportQuestionFormData.header || !supportQuestionFormData.answer) {
                        setErrorDialogMessage('Fyll i både rubrik och svar');
                        setErrorDialogOpen(true);
                        return;
                      }
                      
                      try {
                        if (editingSupportQuestion) {
                          await supportQuestionService.updateSupportQuestion(
                            editingSupportQuestion.supportQuestionID, 
                            supportQuestionFormData
                          );
                        } else {
                          await supportQuestionService.createSupportQuestion(supportQuestionFormData);
                        }
                        const res = await supportQuestionService.getSupportQuestions();
                        setSupportQuestions(res.data);
                        setEditingSupportQuestion(null);
                        setSupportQuestionFormData({ header: "", answer: "" });
                        setIsFormOpen(false);
                      } catch (error) {
                        console.error(error);
                        setErrorDialogMessage('Ett fel uppstod vid sparande av fråga');
                        setErrorDialogOpen(true);
                      }
                    }}
                  >
                    {editingSupportQuestion ? 'Uppdatera' : 'Skapa'}
                  </Button>
                  <Button
                    appearance="secondary"
                    onClick={() => {
                      setEditingSupportQuestion(null);
                      setSupportQuestionFormData({ header: "", answer: "" });
                      setIsFormOpen(false);
                    }}
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            ) : null}
            
            <div className="mt-6">
              <Text weight="semibold" size={400} className="block mb-2">
                Befintliga frågor
              </Text>
              {supportQuestions.map(sq => (
                <div key={sq.supportQuestionID} className={`mb-3 p-3 border border-gray-300 rounded ${editingSupportQuestion?.supportQuestionID === sq.supportQuestionID ? 'bg-yellow-100' : 'bg-white'}`}>
                  <div className="mb-2">
                    <Text weight="semibold">{sq.header}</Text>
                  </div>
                  <div className="mb-2 text-sm text-gray-600">
                    <div className="rich-text-content" dangerouslySetInnerHTML={{__html: sq.answer}}></div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      appearance="outline"
                      onClick={() => {
                        setEditingSupportQuestion(sq);
                        setSupportQuestionFormData({ header: sq.header, answer: sq.answer });
                        setIsFormOpen(true);
                      }}
                    >
                      Redigera
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => {
                        setQuestionToDelete(sq.supportQuestionID);
                        setDeleteQuestionConfirmOpen(true);
                      }}
                    >
                      Ta bort
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => {
              setEditSupportQuestionsDialogOpen(false);
              setEditingSupportQuestion(null);
              setSupportQuestionFormData({ header: "", answer: "" });
              setIsFormOpen(false);
            }}>Stäng</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={editContactHtmlDialogOpen} onOpenChange={(_, data) => setEditContactHtmlDialogOpen(data.open)}>
      <DialogSurface className="max-w-[800px]">
        <DialogBody>
          <DialogTitle>Redigera kontaktinformation</DialogTitle>
          <DialogContent>
            <Field>
              <div onKeyDown={(e) => e.stopPropagation()}>
                <ReactQuill
                  theme="snow"
                  className='rounded-quill-editor mb-10'
                  value={editedContactHtml}
                  onChange={(e) => setEditedContactHtml(e)}
                  placeholder="Skriv brödtext här..."
                />
              </div>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setEditContactHtmlDialogOpen(false)}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={saveContactHtml}
              disabled={!loaded}
            >
              {!loaded ? <Spinner size="tiny" /> : 'Spara'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={errorDialogOpen} onOpenChange={(_, data) => setErrorDialogOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Fel</DialogTitle>
          <DialogContent>
            <Text>{errorDialogMessage}</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setErrorDialogOpen(false)}>OK</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={successDialogOpen} onOpenChange={(_, data) => setSuccessDialogOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Framgång</DialogTitle>
          <DialogContent>
            <Text>{successDialogMessage}</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setSuccessDialogOpen(false)}>OK</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <Dialog open={deleteQuestionConfirmOpen} onOpenChange={(_, data) => setDeleteQuestionConfirmOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Bekräfta borttagning</DialogTitle>
          <DialogContent>
            <Text>Är du säker på att du vill ta bort denna fråga?</Text>
          </DialogContent>
          <DialogActions>
            <Button 
              appearance="secondary" 
              onClick={() => {
                setDeleteQuestionConfirmOpen(false);
                setQuestionToDelete(null);
              }}
            >
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={async () => {
                if (!questionToDelete) return;
                
                setDeleteQuestionConfirmOpen(false);
                try {
                  await supportQuestionService.deleteSupportQuestion(questionToDelete);
                  const res = await supportQuestionService.getSupportQuestions();
                  setSupportQuestions(res.data);
                  setSuccessDialogMessage('Frågan har tagits bort');
                  setSuccessDialogOpen(true);
                } catch (error) {
                  console.error(error);
                  setErrorDialogMessage('Ett fel uppstod vid borttagning av frågan');
                  setErrorDialogOpen(true);
                } finally {
                  setQuestionToDelete(null);
                }
              }}
            >
              Ta bort
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    </>
  );
};

export default SupportDrawer;
